import { useState, useRef, useEffect } from 'react'

import { ActionIcon, Divider, Loader, TextInput } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  UPDATE_DOCUMENT_TEMPLATE_PACKAGE,
  GET_DOCUMENT_TEMPLATE_PACKAGES,
  GET_DOCUMENT_TEMPLATE_PACKAGE,
} from 'src/graphql/documentTemplatePackages.graphql'
import IconLeftArrow from 'src/icons/IconLeftArrow'

import SettingsField from '../../components/SettingsField'

import AddDocumentTemplateRow from './components/AddDocumentTemplateRow'
import DeletePackage from './components/DeletePackage'
import DocumentTemplateRow from './components/DocumentTemplateRow'
import DocumentTemplateDetailsPage from './DocumentTemplateDetails'

const DEBOUNCE_DELAY = 500

const DocumentPackageDetailsPage = ({
  selectedPackageId,
  setSelectedPackageId,
  refetch: refetchPackages,
}) => {
  const [packageName, setPackageName] = useState(undefined)
  const [initialLoad, setInitialLoad] = useState(true)

  const {
    data,
    loading,
    refetch: refetchPackage,
  } = useQuery(GET_DOCUMENT_TEMPLATE_PACKAGE, {
    variables: { documentTemplatePackageId: selectedPackageId },
    onCompleted: (data) => {
      setPackageName(data.documentTemplatePackage.packageName)
      setInitialLoad(false)
    },
  })

  const pkg = data?.documentTemplatePackage
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  )
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const [updatePackage] = useMutation(UPDATE_DOCUMENT_TEMPLATE_PACKAGE, {
    refetchQueries: [GET_DOCUMENT_TEMPLATE_PACKAGES],
    onError: (error) => {
      toast(error.message, 'error')
    },
    onCompleted: () => {
      toast('Package name updated', 'success')
    },
  })

  const handleRefetch = () => {
    refetchPackage()
    refetchPackages()
  }

  useEffect(() => {
    if (!pkg || packageName === pkg.packageName || packageName === undefined)
      return

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    debounceTimeout.current = setTimeout(async () => {
      await updatePackage({
        variables: {
          documentTemplatePackageId: pkg.documentTemplatePackageId,
          input: { packageName },
        },
      })
    }, DEBOUNCE_DELAY)

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageName, pkg])

  // Only show loader on initial load
  if (initialLoad || (loading && !pkg))
    return (
      <div className="flex h-full items-center justify-center">
        <Loader size="xs" color="gray" />
      </div>
    )

  return selectedTemplateId ? (
    <DocumentTemplateDetailsPage
      documentTemplateId={selectedTemplateId}
      setSelectedTemplateId={setSelectedTemplateId}
      refetchPackage={handleRefetch}
    />
  ) : (
    <div className="flex h-full flex-col gap-4 overflow-auto">
      <div className="flex flex-row items-center justify-between">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => setSelectedPackageId(null)}
        >
          <IconLeftArrow className="fill-none stroke-doubleNickel-gray-700" />
        </ActionIcon>
      </div>
      <Divider />
      <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
        <SettingsField title="Package Name" description="Name of the package.">
          <div className="flex items-center gap-2">
            <TextInput
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              size="xs"
              className="min-w-[28rem] flex-1"
            />
          </div>
        </SettingsField>
        <SettingsField
          title="Documents"
          description="Documents included in this package."
        />
        <div>
          <div className="grid grid-cols-11 gap-4 bg-doubleNickel-gray-50 p-2 text-xs font-semibold text-doubleNickel-gray-700">
            <div className="col-span-2">Name</div>
            <div className="col-span-2">Anvil</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Signers</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 justify-self-end">Actions</div>
          </div>
          <div className="flex flex-col">
            {pkg.documentTemplates.map((doc) => {
              return (
                <DocumentTemplateRow
                  key={doc.documentTemplateId}
                  refetch={handleRefetch}
                  pkg={pkg}
                  doc={doc}
                  setSelectedTemplateId={setSelectedTemplateId}
                />
              )
            })}
          </div>
        </div>
        <AddDocumentTemplateRow pkg={pkg} refetch={handleRefetch} />

        <Divider className="mt-auto" />
        <DeletePackage pkg={pkg} setSelectedPackageId={setSelectedPackageId} />
      </div>
    </div>
  )
}

export default DocumentPackageDetailsPage
